/**
 * Browser half of the AES-256-GCM payload envelope. Mirrors
 * `crystal-dex-backend/src/lib/http/payload-crypto.ts` — same algorithm, same wire
 * format, same enable conditions.
 *
 * ── What this actually buys you ──
 * It stops casual inspection of the API in the Network tab and defeats naive
 * scrapers. That is worth something.
 *
 * It does NOT make the API private, and it must not be relied on as if it did:
 * this file holds the key, this file ships to every visitor, and anyone can read
 * the key out of the bundle or simply breakpoint `decryptPayload` and watch the
 * cleartext go by. Confidentiality on the wire is already handled by HTTPS.
 *
 * The real controls remain authorization, rate limiting, and not sending a caller
 * data they are not entitled to. Treat every endpoint as publicly readable.
 */

const ALGO = 'AES-GCM'
const IV_BYTES = 12
export const ENCRYPTION_HEADER = 'x-cdx-enc'

export interface EncryptedEnvelope {
  v: number
  iv: string
  ct: string
  tag: string
}

/**
 * On in production only, and only with a valid 32-byte hex key — matching the
 * server. Development stays plaintext so the Network tab remains readable.
 */
const RAW_KEY = import.meta.env.VITE_PAYLOAD_ENCRYPTION_KEY?.trim()
const KEY_VALID = !!RAW_KEY && /^[0-9a-fA-F]{64}$/.test(RAW_KEY)

export const payloadEncryptionEnabled = import.meta.env.PROD && KEY_VALID

if (import.meta.env.PROD && RAW_KEY && !KEY_VALID) {
  // Loud rather than silent: a malformed key that quietly disables encryption is
  // worse than none, because you would ship believing it was on.
  console.error(
    '[crystal] VITE_PAYLOAD_ENCRYPTION_KEY must be 64 hex characters (32 bytes). Payload encryption is DISABLED.',
  )
}

let keyPromise: Promise<CryptoKey> | null = null

function getKey(): Promise<CryptoKey> {
  keyPromise ??= crypto.subtle.importKey(
    'raw',
    hexToBytes(RAW_KEY!),
    { name: ALGO },
    false, // non-extractable: marginal, but costs nothing
    ['encrypt', 'decrypt'],
  )
  return keyPromise
}

function hexToBytes(hex: string): Uint8Array<ArrayBuffer> {
  // Explicit ArrayBuffer: WebCrypto's BufferSource excludes SharedArrayBuffer,
  // and a bare `new Uint8Array(n)` widens to ArrayBufferLike.
  const out = new Uint8Array(new ArrayBuffer(hex.length / 2))
  for (let i = 0; i < out.length; i++) out[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16)
  return out
}

function toBase64(bytes: Uint8Array): string {
  let binary = ''
  for (const b of bytes) binary += String.fromCharCode(b)
  return btoa(binary)
}

function fromBase64(b64: string): Uint8Array<ArrayBuffer> {
  const binary = atob(b64)
  const out = new Uint8Array(new ArrayBuffer(binary.length))
  for (let i = 0; i < binary.length; i++) out[i] = binary.charCodeAt(i)
  return out
}

/**
 * WebCrypto appends the 16-byte auth tag to the ciphertext; Node exposes it
 * separately via `getAuthTag()`. We split it out so both sides speak the same
 * wire format.
 */
export async function encryptPayload(plaintext: string): Promise<EncryptedEnvelope> {
  const key = await getKey()
  const iv = crypto.getRandomValues(new Uint8Array(IV_BYTES))

  const sealed = new Uint8Array(
    await crypto.subtle.encrypt({ name: ALGO, iv }, key, new TextEncoder().encode(plaintext)),
  )

  const tagStart = sealed.length - 16
  return {
    v: 1,
    iv: toBase64(iv),
    ct: toBase64(sealed.slice(0, tagStart)),
    tag: toBase64(sealed.slice(tagStart)),
  }
}

/** Returns null on any failure so the caller can fall back rather than crash. */
export async function decryptPayload(envelope: EncryptedEnvelope): Promise<string | null> {
  try {
    const key = await getKey()
    const ct = fromBase64(envelope.ct)
    const tag = fromBase64(envelope.tag)

    // Re-join ciphertext and tag into the single buffer WebCrypto expects.
    const sealed = new Uint8Array(new ArrayBuffer(ct.length + tag.length))
    sealed.set(ct)
    sealed.set(tag, ct.length)

    const plain = await crypto.subtle.decrypt(
      { name: ALGO, iv: fromBase64(envelope.iv) },
      key,
      sealed,
    )
    return new TextDecoder().decode(plain)
  } catch {
    return null
  }
}

export function looksEncrypted(value: unknown): value is EncryptedEnvelope {
  return (
    typeof value === 'object' &&
    value !== null &&
    'ct' in value &&
    'iv' in value &&
    'tag' in value
  )
}
